import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, OnInit } from "@angular/core";
import { ControlContainer, NgForm } from '@angular/forms';
import { MatLegacyDialog as MatDialog }from '@angular/material/legacy-dialog';
import { TranslationService } from '@sdbn/common';
import { ResponseMessage } from '@sdbn/appclient';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { DialogLayout } from 'src/app/shared/dialog-layout/dialog-layout';
import { TableSelectionDialogComponent, TableSelectionDialogData } from 'src/tmc-common/components/table-selection-dialog/table-selection-dialog.component';
import { ColumnType } from '@sdbn/appclient';
import { Row } from '@sdbn/appclient';
import { SelectionType } from '@sdbn/appclient';
import { TmcTableModel } from '@sdbn/appclient';
import { BackupStationSet, Station, StationGroup, StationSet, StationSetType, StationType } from '../../model/yard-structure.model';
import { StationgroupService } from '../../service/stationgroup.service';

@Component({
  selector: 'app-stationset',
  templateUrl: './stationset-component.html',
  styleUrls: ['./stationset-component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})

export class StationSetComponent implements OnInit {

  stationSetTypes: StationSetType[] = [];

  newId = -1;

  activeTabIndex = 0;

  keys = Object.keys;
  stationTypes = StationType;
  stations: Station[];
  displayedColumns = ['name', 'description', 'coordinates', 'deleteStation'];
  /** Shows loading spinner i.e. when deletion is triggered and validation request is running. */
  requestRunning = false;

  /** Messages to display, i.e. validation errors. */
  responseMessages: ResponseMessage[];

  @Input() stationSet: StationSet;
  @Input() stationGroup: StationGroup;
  @Input() form: NgForm;

  constructor(
    public translationService: TranslationService,
    private stationgroupService: StationgroupService,
    private dialog: MatDialog
  ) {

    for (const k of Object.keys(StationSetType)) {
      this.stationSetTypes.push(StationSetType[k]);
    }
  }

  ngOnInit() {
    this.stationgroupService.getStations().subscribe(
      response => {
        this.stations = response;
      },
      error => {
        console.log('Error while getting yard overview!', error);
      });
  }

  public addStationToStationSet() {
    if (this.stations != null) {
      const assignedStationIds = this.stationSet.stations.map(station => station.id);
      const assignableStations = this.stations.filter(station => assignedStationIds.indexOf(station.id) === -1);

      const tableSelectionDialogData: TableSelectionDialogData = {
        title: this.translationService.translate('tmc.stationgroup.stationset.stations.dialog.title'),
        tableModel: this.createTableModelForStationSelectionDialog(assignableStations),
        confirmCallback: (tableModel: TmcTableModel) => {
          this.addStationsToStationSet(tableModel.selectedRows);
        }
      };

      this.dialog.open(TableSelectionDialogComponent, {
        data: tableSelectionDialogData,
        panelClass: DialogLayout.panelClass
      });
    }
  }

  private addStationsToStationSet(selectedRows: Row[]) {
    // add selectedRows to stationSet and refresh table
    const selectedStations = this.convertRowsToStations(selectedRows);
    this.stationSet.stations.push(...selectedStations);
    this.updatePositionsIndex();
    this.form.form.markAsDirty();
  }

  private createTableModelForStationSelectionDialog(assignableStations: Station[]): TmcTableModel {
    const tableModel = new TmcTableModel();
    tableModel.selectionType = SelectionType.MULTIPLE;
    tableModel.sortable = true;
    tableModel.filterable = true;
    tableModel.sortColumn = 'name';
    tableModel.sortDirection = 'asc';
    const prefix = 'tmc.stationgroup.stationset.stations.table.column.';
    tableModel.columnDefinitions = [
      { id: 'name', name: 'name', visible: true, type: ColumnType.STRING, translationKey: prefix + 'name' },
      { id: 'description', name: 'description', visible: true, type: ColumnType.STRING, translationKey: prefix + 'description' },
      { id: 'stationtype', name: 'stationtype', visible: true, type: ColumnType.STRING, translationKey: prefix + 'stationtype' }
    ];
    tableModel.setRows(this.convertStationsToRows(assignableStations));
    return tableModel;
  }


  private convertStationsToRows(assignableStations: Station[]): Row[] {
    const rows: Row[] = [];
    for (const station of assignableStations) {
      const row: Row = new Row();
      row.id = station.id + '';
      row.values = {
        id: station.id,
        name: station.name,
        description: station.description,
        stationtype: this.translationService.translate('tmc.stationgroup.stationset.stationType.' + station.stationType),
      };

      rows.push(row);
    }
    return rows;
  }

  private convertRowsToStations(selectedRows: Row[]): Station[] {
    return selectedRows.map(row => {
      const station: Station = new Station();
      station.id = row.values.id;
      station.description = row.values.description;
      station.name = row.values.name;
      return station;
    });
  }

  public removeStationFromStationSet(station: Station) {

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc.admin.yard-structure.confirm-dialog.title',
      messageKey: 'tmc.admin.yard-structure.delete-station.confirm-dialog.message',
      confirm: () => {
        if (station.id) {
          this.stationSet.deletedStations.push(station);
        }

        const index = this.stationSet.stations.indexOf(station);
        if (index > -1) {
          this.stationSet.stations.splice(index, 1);
        }

        this.updatePositionsIndex();
        this.form.form.markAsDirty();
      }
    };
    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  public removeBackupStationSet(backupStationSet: BackupStationSet) {
    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc.admin.yard-structure.confirm-dialog.title',
      messageKey: 'tmc.admin.yard-structure.delete-backup-link.confirm-dialog.message',
      confirm: () => {
        const index = this.stationSet.backupStationSets.indexOf(backupStationSet);
        if (index !== -1) {
          this.stationSet.backupStationSets.splice(index, 1);
          this.form.form.markAsDirty();
        }
      }
    };
    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  public addBackupStationSet() {
    this.stationgroupService.getBackupStationSets().subscribe(
      statiobackupStationSets => {

        const model = new TmcTableModel();
        model.selectionType = SelectionType.MULTIPLE;
        model.sortable = true;
        model.filterable = true;
        model.sortColumn = 'name';
        model.sortDirection = 'asc';
        const prefix = 'tmc.stationgroup.stationset.backup-station-sets.table.column.';
        model.columnDefinitions = [
          { id: 'name', name: 'name', visible: true, type: ColumnType.STRING, translationKey: prefix + 'name' },
        ];

        const rows: Row[] = [];
        for (const backupStationSet of statiobackupStationSets) {

          const index = this.stationSet.backupStationSets.findIndex(s => s.stationSetId === backupStationSet.stationSetId);
          if (backupStationSet.stationSetId !== this.stationSet.id && index === -1) {
            const row: Row = new Row();
            row.id = backupStationSet.stationSetId + '';
            row.values = {
              id: backupStationSet.stationSetId,
              name: backupStationSet.name,
              stationSet: backupStationSet
            };

            rows.push(row);
          }
        }

        model.setRows(rows);

        const tableSelectionDialogData: TableSelectionDialogData = {
          title: this.translationService.translate('tmc.stationgroup.stationset.backup-station-sets.dialog.title'),
          tableModel: model,
          confirmCallback: (tableModel: TmcTableModel) => {
            for (const selected of tableModel.getSelectedRows()) {
              this.stationSet.backupStationSets.push(selected.values.stationSet);
            }
            this.form.form.markAsDirty();
          }
        };

        this.dialog.open(TableSelectionDialogComponent, {
          data: tableSelectionDialogData,
          panelClass: DialogLayout.panelClass
        });

      },
      error => {
        console.log('Error while getting yard overview!', error);
      }
    );
  }

  public dropStation(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.stationSet.stations, event.previousIndex, event.currentIndex);
    this.updatePositionsIndex();
    this.form.form.markAsDirty();
  }

  public updatePositionsIndex() {
    for (let i = 0; i < this.stationSet.stations.length; i++) {
      this.stationSet.stations[i].index = i;
    }
  }

  public selectedTabChanged(idx: number): void {
    this.activeTabIndex = idx;
  }

}
